import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UnauthorizedTypes } from '../enums/auth';
import { EnvConfigService } from '../../../shared/services/env-config.service';

export const httpClientInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);
  const handler = inject(HttpClientHandler);
  const env = inject(EnvConfigService);
  let isApiKeyRequest = true;
  if (request.url.startsWith(env.getConfig().apiUrl)) {
    const isAuthenticated = authService.isAuthenticated();
    const apiKey = authService.getQueryStringApiKey();
    if (apiKey) {
      request = request.clone({ setHeaders: { ApiKey: `${encodeURIComponent(apiKey)}` } });
    } else if (isAuthenticated) {
      isApiKeyRequest = false;
      request = request.clone({
        setHeaders: {
          Authorization: `${authService.getAuthToken()}`,
          IdToken: `${authService.getIdToken()}`,
        },
      });
    }
  }
  return next(request).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (error.error && (error.error.data ?? error.error.Data ?? error.error).unauthorizedType == UnauthorizedTypes.InvalidCredentials) {
          authService.logout();
          return next(request);
        } else {
          return handler.handleUnauthorizedError(request, next, isApiKeyRequest, authService, env);
        }
      } else throw error;
    }),
    finalize(() => {}),
  );
};

@Injectable({
  providedIn: 'root',
})
class HttpClientHandler {
  //#region Helpers
  handleUnauthorizedError(request: HttpRequest<any>, next: HttpHandlerFn, isApiKeyRequest: boolean, authService: AuthService, env: EnvConfigService): Observable<HttpEvent<any>> {
    if (!isApiKeyRequest) {
      return from(authService.refreshToken()).pipe(
        switchMap((response: any) => {
          const newAuthToken = response.data.accessToken;
          const modifiedReq = request.clone({
            headers: request.headers.set('Authorization', `${newAuthToken}`),
          });
          return next(modifiedReq);
        }),
        catchError(error => {
          authService.logout();
          const router = inject(Router);
          if (env.getConfig().isRunAsLibrary) {
            router.navigate(['/dispatch/auth']);
          } else {
            router.navigate(['/auth']);
          }
          return throwError(() => error);
        }),
      );
    }
    authService.logout();
    return throwError(() => 'Client Not Authenticated!');
  }
  //#endregion
}
